/**
 * Assistant insight inbox data hooks (OS Buddy proactive assistant — issue #1090).
 *
 * Server-backed via the permission-checked `/api/v1/assistant/insights` API
 * (issue #1089 PR2), so inbox state persists across refresh, re-login and
 * devices. The list endpoint returns only non-shadow insights; RBAC
 * (required_permissions) is enforced server-side.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export type InsightUrgency = 'critical' | 'high' | 'normal' | 'low';
export type InsightConfidence = 'low' | 'medium' | 'high';
export type InsightStatus =
  | 'detected'
  | 'eligible'
  | 'delivered'
  | 'viewed'
  | 'accepted'
  | 'dismissed'
  | 'snoozed'
  | 'expired'
  | 'superseded';
export type InsightFeedbackKind =
  | 'accepted'
  | 'dismissed'
  | 'not_relevant'
  | 'already_handled'
  | 'mute_type';

export interface InsightEvidence {
  sourceEventIds: string[];
  facts: Record<string, unknown>;
}

export interface InsightTargetEntity {
  type: string;
  id: string;
}

export interface AssistantInsight {
  id: string;
  insightType: string;
  ruleVersion: number;
  status: InsightStatus;
  urgency: InsightUrgency;
  confidence: InsightConfidence;
  titleKey: string;
  bodyKey: string;
  messageParams: Record<string, unknown>;
  evidence: InsightEvidence;
  sourceEventIds: string[];
  targetEntities: InsightTargetEntity[];
  deepLink: string | null;
  requiredPermissions: string[];
  cooldownUntil: string | null;
  expiresAt: string | null;
  snoozedUntil: string | null;
  suggestedAction: unknown | null;
  feedbackKind: InsightFeedbackKind | null;
  feedbackComment: string | null;
  createdAt: string;
  viewedAt: string | null;
  handledAt: string | null;
  updatedAt: string;
}

interface InsightListResponse {
  insights: AssistantInsight[];
  limit: number;
  offset: number;
}

export interface InsightFilters {
  [key: string]: string | number | undefined;
  status?: InsightStatus;
  type?: string;
  limit?: number;
  offset?: number;
}

/** Statuses that still belong in the active inbox view. */
const OPEN_STATUSES: ReadonlySet<InsightStatus> = new Set([
  'detected',
  'eligible',
  'delivered',
  'viewed',
  'snoozed',
]);

/**
 * True when an insight should be shown in the active inbox: open, past any snooze,
 * and not lapsed. The API already filters expired insights out (#1214); the check
 * is repeated here so a cached list cannot keep showing one that lapses mid-session.
 */
export function isActiveInsight(insight: AssistantInsight, now: number = Date.now()): boolean {
  if (!OPEN_STATUSES.has(insight.status)) return false;
  if (insight.expiresAt && new Date(insight.expiresAt).getTime() <= now) return false;
  if (insight.status === 'snoozed' && insight.snoozedUntil) {
    return new Date(insight.snoozedUntil).getTime() <= now;
  }
  return true;
}

export function useInsights(filters: InsightFilters = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.insights.list(filters),
    queryFn: () => api.get<InsightListResponse>('/assistant/insights', { params: filters }),
    staleTime: 15 * 1000,
    enabled: options?.enabled,
  });
}

export function useInsight(id: string | null) {
  return useQuery({
    queryKey: queryKeys.insights.detail(id ?? ''),
    queryFn: () => api.get<AssistantInsight>(`/assistant/insights/${id ?? ''}`),
    enabled: !!id,
  });
}

export function useInsightFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; kind: InsightFeedbackKind; comment?: string }) =>
      api.post<AssistantInsight>(`/assistant/insights/${vars.id}/feedback`, {
        kind: vars.kind,
        comment: vars.comment,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.insights.all });
    },
  });
}

export function useInsightSnooze() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; minutes?: number; until?: string }) =>
      api.post<AssistantInsight>(`/assistant/insights/${vars.id}/snooze`, {
        minutes: vars.minutes,
        until: vars.until,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.insights.all });
    },
  });
}

export interface MutedInsightType {
  insightType: string;
  createdAt: string;
}

/**
 * Insight types this user has muted (#1214). `mute_type` feedback adds one; the
 * server leaves muted types out of the inbox list until the user removes it.
 */
export function useMutedInsightTypes(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.insights.mutedTypes(),
    queryFn: () => api.get<{ mutedTypes: MutedInsightType[] }>('/assistant/insights/muted-types'),
    staleTime: 60 * 1000,
    enabled: options?.enabled,
  });
}

export function useUnmuteInsightType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (insightType: string) =>
      api.delete<{ insightType: string; muted: boolean }>(
        `/assistant/insights/muted-types/${encodeURIComponent(insightType)}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.insights.all });
    },
  });
}
